<template>
    <div>
        <!-- Persistent live region. The project rule is to route post-action feedback
             through useToast(), but <AppToast> is mounted per page and this banner is
             mounted once at the app root, so a toast would be silently dropped on any
             page without it. The region is always in the DOM so screen readers pick up
             the message when it changes. -->
        <p class="sr-only" role="status" aria-live="polite">{{ announcement }}</p>

        <Transition
            appear
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="translate-y-full opacity-0"
            leave-active-class="transition duration-200 ease-in"
            leave-to-class="translate-y-full opacity-0"
        >
            <div v-if="isOpen" class="pointer-events-none fixed inset-x-0 bottom-0 z-50 p-3 sm:p-5">
                <section
                    ref="panel"
                    role="region"
                    aria-label="Cookie consent"
                    tabindex="-1"
                    class="card pointer-events-auto relative mx-auto max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-primary/30 bg-base-100 shadow-2xl"
                    @keydown.esc="onEscape"
                >
                    <DotGridPattern
                        class="absolute inset-0 h-full w-full text-primary/10"
                        :spacing="24"
                        :size="1"
                    />
                    <div
                        class="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-primary/10 to-transparent"
                        aria-hidden="true"
                    ></div>

                    <div class="card-body relative gap-5 p-5 sm:p-7">
                        <div class="flex items-start gap-4">
                            <IconBadge :icon="PhCookie" tone="primary" size="lg" />
                            <div class="min-w-0 flex-1">
                                <SectionEyebrow
                                    :icon="PhShieldCheck"
                                    label="Your privacy"
                                    mb="mb-1"
                                />
                                <h2
                                    class="text-xl font-bold leading-tight tracking-tight sm:text-2xl"
                                >
                                    <template v-if="showDetails">
                                        Manage your
                                        <span class="text-primary">cookie</span> choices.
                                    </template>
                                    <template v-else>
                                        You choose what we
                                        <span class="text-primary">measure</span>.
                                    </template>
                                </h2>
                                <!-- The category tiles say all of this in the detail view,
                                     and dropping it keeps the actions above the fold on a
                                     small phone. -->
                                <p
                                    v-if="!showDetails"
                                    class="mt-2 text-sm leading-relaxed text-base-content/70"
                                >
                                    Essential cookies keep Pokor running — signing you in, holding
                                    your session, remembering your settings. Analytics only load if
                                    you say yes, and you can change your mind any time.
                                </p>
                                <!-- Plain <a>: the privacy route is domain-scoped, so its
                                     Wayfinder URL is absolute and an Inertia <Link> would turn
                                     the click into a blocked cross-origin XHR on app.* -->
                                <a
                                    :href="privacyUrl"
                                    class="link link-primary mt-2 inline-flex items-center gap-1 text-sm font-medium"
                                >
                                    Read the privacy policy
                                    <PhArrowSquareOut :size="14" aria-hidden="true" />
                                </a>
                            </div>
                        </div>

                        <div
                            v-if="showDetails"
                            role="group"
                            aria-label="Cookie categories"
                            class="grid gap-3 sm:grid-cols-2"
                        >
                            <div class="rounded-box border border-base-300 bg-base-200/60 p-4">
                                <div class="flex items-start justify-between gap-3">
                                    <IconBadge :icon="PhShieldCheck" tone="primary" size="sm" />
                                    <div class="flex items-center gap-2">
                                        <span class="text-xs font-semibold text-base-content/70"
                                            >On</span
                                        >
                                        <input
                                            id="cookie-consent-essential"
                                            type="checkbox"
                                            class="toggle toggle-sm"
                                            checked
                                            disabled
                                        />
                                    </div>
                                </div>
                                <div class="mt-3 flex flex-wrap items-center gap-2">
                                    <label
                                        for="cookie-consent-essential"
                                        class="text-sm font-semibold text-base-content"
                                        >Essential</label
                                    >
                                    <span
                                        class="badge badge-xs border border-base-300 bg-base-100 text-base-content"
                                        >Always on</span
                                    >
                                </div>
                                <p class="mt-1 text-xs leading-relaxed text-base-content/70">
                                    Sign-in, CSRF protection, guest identity and saved preferences.
                                </p>
                            </div>

                            <div class="rounded-box border border-base-300 bg-base-200/60 p-4">
                                <div class="flex items-start justify-between gap-3">
                                    <IconBadge :icon="PhChartLine" tone="primary" size="sm" />
                                    <div class="flex items-center gap-2">
                                        <span class="text-xs font-semibold text-base-content/70">{{
                                            draft.analytics ? 'On' : 'Off'
                                        }}</span>
                                        <input
                                            id="cookie-consent-analytics"
                                            v-model="draft.analytics"
                                            type="checkbox"
                                            class="toggle toggle-sm toggle-primary"
                                        />
                                    </div>
                                </div>
                                <div class="mt-3">
                                    <label
                                        for="cookie-consent-analytics"
                                        class="text-sm font-semibold text-base-content"
                                        >Analytics</label
                                    >
                                </div>
                                <p class="mt-1 text-xs leading-relaxed text-base-content/70">
                                    PostHog product analytics for page views and feature usage.
                                </p>
                            </div>
                        </div>

                        <div
                            class="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between"
                        >
                            <button
                                v-if="showDetails"
                                type="button"
                                class="btn btn-ghost w-full sm:w-auto"
                                @click="showDetails = false"
                            >
                                <PhArrowLeft :size="16" aria-hidden="true" />
                                Back
                            </button>
                            <button
                                v-else
                                type="button"
                                class="btn btn-ghost w-full sm:w-auto"
                                @click="showDetails = true"
                            >
                                Manage choices
                            </button>

                            <!-- Reject keeps the same weight as accept: a de-emphasised
                                 reject is what EDPB guidance calls out as non-compliant. -->
                            <div class="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
                                <button
                                    type="button"
                                    class="btn btn-neutral w-full sm:w-auto"
                                    @click="rejectOptional"
                                >
                                    Reject optional
                                </button>
                                <button
                                    v-if="showDetails"
                                    type="button"
                                    class="btn btn-primary w-full sm:w-auto"
                                    @click="save"
                                >
                                    Save choices
                                </button>
                                <button
                                    v-else
                                    type="button"
                                    class="btn btn-primary w-full sm:w-auto"
                                    @click="acceptAll"
                                >
                                    Accept all
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue';
import {
    PhArrowLeft,
    PhArrowSquareOut,
    PhChartLine,
    PhCookie,
    PhShieldCheck,
} from '@phosphor-icons/vue';
import IconBadge from '@/components/design/IconBadge.vue';
import SectionEyebrow from '@/components/design/SectionEyebrow.vue';
import DotGridPattern from '@/components/icons/DotGridPattern.vue';
import { privacy } from '@/routes';
import { useCookieConsent } from '@/composables/useCookieConsent';
import { useCookiePreferencesBus } from '@/composables/buses';

const privacyUrl = privacy.url();
const {
    consent,
    hasChoice,
    acceptAll: acceptAllConsent,
    rejectOptional: rejectOptionalConsent,
    savePreferences,
} = useCookieConsent();

const panel = ref<HTMLElement | null>(null);

// This component is mounted once for the lifetime of the app (see app.ts), so
// every piece of state here has to be derived or explicitly reset — a one-shot
// ref would stay stuck on whatever it read at bootstrap.
const forcedOpen = ref(false);
const isOpen = computed(() => forcedOpen.value || !hasChoice.value);
const showDetails = ref(false);
const announcement = ref('');
const draft = reactive({
    analytics: consent.value?.analytics === true,
});

// Only set when the banner was opened deliberately from the footer's "Cookie
// Settings" link — that is the one case where focus has to go back somewhere.
// On a first visit nothing is moved, so accepting doesn't yank a mouse user's
// focus to the skip link.
let returnFocusTo: HTMLElement | null = null;

watch(consent, (value) => {
    draft.analytics = value?.analytics === true;
});

function announce(message: string): void {
    announcement.value = '';
    void nextTick(() => {
        announcement.value = message;
    });
}

function close(): void {
    forcedOpen.value = false;
    showDetails.value = false;
    void nextTick(() => {
        // The footer that owns the trigger can unmount on an Inertia visit.
        if (returnFocusTo?.isConnected) {
            returnFocusTo.focus();
        }
        returnFocusTo = null;
    });
}

function announceSaved(analyticsAllowed: boolean): void {
    announce(`Cookie preferences saved. Analytics cookies are ${analyticsAllowed ? 'on' : 'off'}.`);
}

function acceptAll(): void {
    acceptAllConsent();
    announceSaved(true);
    close();
}

function rejectOptional(): void {
    rejectOptionalConsent();
    announceSaved(false);
    close();
}

function save(): void {
    savePreferences(draft);
    announceSaved(draft.analytics);
    close();
}

function onEscape(): void {
    // Esc must not dismiss the first-visit prompt: the banner never remounts, so
    // the choice would be unreachable for the rest of the session.
    if (forcedOpen.value) {
        close();
    }
}

function openPreferences(): void {
    returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    draft.analytics = consent.value?.analytics === true;
    forcedOpen.value = true;
    showDetails.value = true;
    void nextTick(() => panel.value?.focus());
}

// VueUse's bus registers its off-handle on the current effect scope, so this
// cleans itself up if the component is ever unmounted.
useCookiePreferencesBus().on(openPreferences);
</script>
